
const DB_NAME = 'ai_memory_db';
const DB_VERSION = 2; // Incremented version for new store
const STORE_NAME = 'core_attributes';
const SESSIONS_STORE = 'chat_sessions';

class MemoryDB {
  constructor() {
    this.db = null;
    this.ready = this.init();
  }

  init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
        if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
          db.createObjectStore(SESSIONS_STORE, { keyPath: 'id' });
        }
      };
    });
  }

  // Core Attributes (Soul, User, Memory)
  async get(key) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async set(key, value) {
    await this.ready;
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        // Ensure value is a plain object to avoid DataCloneError with Vue reactive objects
        const plainValue = JSON.parse(JSON.stringify(value));
        const request = store.put(plainValue, key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      } catch (e) {
        console.error('[DEBUG-DB] set transaction error:', e);
        reject(e);
      }
    });
  }

  // Sessions Management
  async getAllSessions() {
    await this.ready;
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([SESSIONS_STORE], 'readonly');
      const store = transaction.objectStore(SESSIONS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        // Sort by timestamp descending
        const sessions = request.result || [];
        sessions.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        resolve(sessions);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async saveSession(session) {
    await this.ready;
    console.log('[DEBUG-DB] saveSession executing for id:', session.id);
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction([SESSIONS_STORE], 'readwrite');
        const store = transaction.objectStore(SESSIONS_STORE);
        
        // Ensure session is a plain object to avoid DataCloneError with Vue reactive objects
        const plainSession = JSON.parse(JSON.stringify(session));
        
        // Use put without key param because keyPath is defined
        const request = store.put(plainSession);

        request.onsuccess = () => {
            console.log('[DEBUG-DB] saveSession success');
            resolve();
        };
        request.onerror = (e) => {
            console.error('[DEBUG-DB] saveSession error:', e.target.error);
            reject(request.error);
        };
      } catch (e) {
        console.error('[DEBUG-DB] saveSession transaction error:', e);
        reject(e);
      }
    });
  }

  async deleteSession(id) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([SESSIONS_STORE], 'readwrite');
      const store = transaction.objectStore(SESSIONS_STORE);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAll() {
    await this.ready;
    const coreData = await new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.openCursor();
      const results = {};

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          results[cursor.key] = cursor.value;
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      request.onerror = () => reject(request.error);
    });

    const sessions = await this.getAllSessions();
    return { coreData, sessions };
  }

  async clearAll() {
    await this.ready;
    const t1 = this.db.transaction([STORE_NAME], 'readwrite').objectStore(STORE_NAME).clear();
    const t2 = this.db.transaction([SESSIONS_STORE], 'readwrite').objectStore(SESSIONS_STORE).clear();
    return Promise.all([
      new Promise((res, rej) => { t1.onsuccess = res; t1.onerror = rej; }),
      new Promise((res, rej) => { t2.onsuccess = res; t2.onerror = rej; })
    ]);
  }

  async importAll(data, merge = false) {
    await this.ready;
    
    let existingSessions = [];
    if (merge) {
      // Fetch existing sessions before clearing coreData
      existingSessions = await this.getAllSessions();
      // Only clear core_attributes store when merging, keep sessions store intact
      const t1 = this.db.transaction([STORE_NAME], 'readwrite').objectStore(STORE_NAME).clear();
      await new Promise((res, rej) => { t1.onsuccess = res; t1.onerror = rej; });
    } else {
      await this.clearAll();
    }
    
    const coreData = data.coreData || data; // Fallback for old backup format
    const sessions = data.sessions || [];

    // Merge sessions if requested
    let finalSessions = sessions;
    if (merge && existingSessions.length > 0) {
      const sessionMap = new Map();
      // Add existing sessions first
      existingSessions.forEach(s => sessionMap.set(s.id, s));
      // Overwrite with imported sessions (or add new ones)
      sessions.forEach(s => sessionMap.set(s.id, s));
      finalSessions = Array.from(sessionMap.values());
    }

    const p1 = Object.entries(coreData)
      .filter(([key]) => key !== 'sessions')
      .map(([key, value]) => this.set(key, value));
    const p2 = finalSessions.map(session => this.saveSession(session));

    return Promise.all([...p1, ...p2]);
  }
}

export const memoryDB = new MemoryDB();
